import React, { useEffect, useRef, useState } from 'react';

import { Box, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { FiClock, FiCreditCard, FiUsers } from 'react-icons/fi';

import { INVITE_MODULES_CONFIG } from '../constants';
import { InviteSelectionApi } from '../hooks/useInviteSelection';
import { InviteTargetsApi } from '../hooks/useInviteTargets';
import { InviteModuleKey } from '../types';
import { SelectionSummary } from './SelectionSummary';
import { PayrollTargetSection } from './targets/PayrollTargetSection';
import { TargetSection } from './targets/TargetSection';
import { TeamsTargetSection } from './targets/TeamsTargetSection';
import { TimeTargetSection } from './targets/TimeTargetSection';

interface Props {
  selection: InviteSelectionApi;
  targets: InviteTargetsApi;
}

export const WhereStep: React.FC<Props> = ({ selection, targets }) => {
  const theme = useTheme();
  const { state, visibleModules, preselectedModule, ready } = targets;
  // Every module starts collapsed so all of their headers are visible at once. The exception is
  // the module the dialog was opened from — that is where its resource is already ticked
  const moduleToOpen = preselectedModule ?? null;
  const [expanded, setExpanded] = useState<InviteModuleKey | null>(moduleToOpen);
  // Only one module is opened automatically — after that a closed section stays closed,
  // otherwise collapsing it would just reopen it
  const autoExpandedRef = useRef<boolean>(!!moduleToOpen);

  // Inventories and permissions may settle after mount — open the module once they do
  useEffect(() => {
    if (!autoExpandedRef.current && moduleToOpen) {
      autoExpandedRef.current = true;
      setExpanded(moduleToOpen);
    }
  }, [moduleToOpen]);

  const toggle = (module: InviteModuleKey) => setExpanded(value => (value === module ? null : module));

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <SelectionSummary selection={selection} />

      {!ready && (
        <Box display="flex" flexDirection="column" gridGap={8}>
          {[0, 1].map(index => (
            <Skeleton
              key={index}
              variant="rect"
              height={56}
              style={{ borderRadius: 8, backgroundColor: theme.palette.action.hover }}
            />
          ))}
        </Box>
      )}

      {visibleModules.includes('teams') && (
        <TargetSection
          title={INVITE_MODULES_CONFIG.teams.label}
          description={INVITE_MODULES_CONFIG.teams.description}
          icon={<FiUsers size="18px" color={theme.palette.primary.main} />}
          selectedCount={state.teamIds.length}
          expanded={expanded === 'teams'}
          onToggle={() => toggle('teams')}
        >
          <TeamsTargetSection
            teams={targets.teamOptions}
            selectedIds={state.teamIds}
            onToggle={targets.toggleTeam}
            onToggleAll={targets.toggleAllTeams}
          />
        </TargetSection>
      )}

      {visibleModules.includes('time') && (
        <TargetSection
          title={INVITE_MODULES_CONFIG.time.label}
          description={INVITE_MODULES_CONFIG.time.description}
          icon={<FiClock size="18px" color={theme.palette.secondary.main} />}
          selectedCount={state.locationIds.length}
          expanded={expanded === 'time'}
          onToggle={() => toggle('time')}
          error={targets.locationsMissingShift.length ? 'Choose a shift for every selected location' : null}
        >
          <TimeTargetSection
            locations={targets.locationOptions}
            selectedLocationIds={state.locationIds}
            shiftIdsByLocation={state.shiftIdsByLocation}
            missingShiftIds={targets.locationsMissingShift}
            onToggleLocation={targets.toggleLocation}
            onToggleAllLocations={targets.toggleAllLocations}
            onToggleShift={targets.toggleShift}
            onToggleAllShifts={targets.toggleAllShifts}
          />
        </TargetSection>
      )}

      {visibleModules.includes('payroll') && (
        <TargetSection
          title={INVITE_MODULES_CONFIG.payroll.label}
          description={INVITE_MODULES_CONFIG.payroll.description}
          icon={<FiCreditCard size="18px" color={theme.palette.success.main} />}
          selectedCount={state.payScheduleIds.length}
          expanded={expanded === 'payroll'}
          onToggle={() => toggle('payroll')}
          error={targets.payrollMissingScheme ? 'Choose how to engage them' : null}
        >
          <PayrollTargetSection
            paySchedules={targets.payScheduleOptions}
            selectedIds={state.payScheduleIds}
            payScheme={state.payScheme}
            showSchemeError={targets.payrollMissingScheme}
            onToggle={targets.togglePaySchedule}
            onToggleAll={targets.toggleAllPaySchedules}
            onSchemeChange={targets.setPayScheme}
          />
        </TargetSection>
      )}
    </Box>
  );
};
